import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, X, ArrowRight, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Banner displayed at the top of the salon dashboard while the 14-day trial is active.
 * Becomes urgent (red gradient) when ≤ 3 days remain.
 * User can dismiss for the current session (per-tab).
 */
export default function TrialBanner() {
  const { trial, plan } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissal when the trial state itself changes (e.g. fresh login)
    setDismissed(false);
  }, [trial?.ends_at]);

  if (!trial || !trial.active || dismissed) return null;
  const days = trial.days_left ?? 0;
  const urgent = days <= 3;

  return (
    <div
      data-testid="trial-banner"
      className="relative px-4 py-3 text-sm flex items-center gap-3 border-b"
      style={{
        background: urgent
          ? "linear-gradient(90deg, #fee2e2 0%, #fecaca 100%)"
          : "linear-gradient(90deg, rgba(20,184,166,0.10) 0%, rgba(6,182,212,0.10) 100%)",
        borderColor: urgent ? "#fca5a5" : "var(--mv-border)",
        color: urgent ? "#7f1d1d" : "var(--mv-text)",
      }}
    >
      {urgent ? <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={2} /> : <Clock className="w-4 h-4 shrink-0" strokeWidth={1.75} />}
      <div className="flex-1 min-w-0">
        <span className="font-medium">
          {days === 0
            ? "Deine Testphase endet heute."
            : days === 1
              ? "Deine Testphase endet morgen."
              : `Deine Testphase endet in ${days} Tagen.`}
        </span>
        <span className="ml-2 opacity-80 hidden sm:inline">
          {plan?.name ? `Aktuelles Paket: ${plan.name} — ` : ""}
          ohne Abschluss wirst du automatisch ins kostenlose Free-Paket gestuft.
        </span>
      </div>
      <Link
        to="/cms/billing"
        data-testid="trial-banner-cta"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap"
        style={{
          background: urgent ? "#dc2626" : "var(--mv-primary)",
          color: "#fff",
        }}
      >
        Jetzt Abo abschließen <ArrowRight className="w-3 h-3" strokeWidth={2} />
      </Link>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="trial-banner-dismiss"
        className="p-1 opacity-60 hover:opacity-100"
        title="Hinweis ausblenden"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
